import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Shield, Heart, User, Settings, Trash2 } from 'lucide-react';
import { Part } from '../../types';
import { emojiColorMap } from '../../lib/emojiLibrary';
import { motion, AnimatePresence } from 'framer-motion';

interface PartCardProps {
  part: Part;
  isSelected: boolean;
  onClick: (partId: string) => void;
  onDelete?: (e: React.MouseEvent) => void;
  integratedParts?: Part[];
}

export default function PartCard({ part, isSelected, onClick, onDelete, integratedParts = [] }: PartCardProps) {
  const style = emojiColorMap[part.color] || emojiColorMap.blue;
  const isSelf = part.type === 'self';
  const [isExpanded, setIsExpanded] = useState(false);

  const handleClick = (e: React.MouseEvent, partId: string) => {
    e.stopPropagation();
    onClick(partId);
  };

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const getPartIcon = () => {
    switch (part.type) {
      case 'self':
        return <User className="h-4 w-4" />;
      case 'protector':
      case 'manager':
        return <Shield className="h-4 w-4" />;
      case 'exile':
      case 'rescuer':
        return <Heart className="h-4 w-4" />;
      default:
        return <Settings className="h-4 w-4" />;
    }
  };

  // Get first paragraph for preview
  const previewText = part.description?.split('\n\n')[0] || '';
  
  // Get remaining paragraphs for expanded view
  const expandedText = part.description?.split('\n\n').slice(1).join('\n\n') || '';

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      className={`
        relative p-6 rounded-xl border-2 transition-all cursor-pointer
        ${style.bg} ${style.border} ${style.hover}
        ${isSelected ? 'ring-2 ring-amber-500 ring-opacity-50' : ''}
      `}
      onClick={(e) => handleClick(e, part.id)}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-4xl transform group-hover:scale-110 transition-transform">
            {part.emoji}
          </span>
          <div>
            <h3 className="text-lg font-bold text-midnight-800">{part.name}</h3>
            <div className="flex items-center space-x-2">
              <div className={`
                inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                ${style.bg} ${style.text}
              `}>
                <div className="flex items-center space-x-1">
                  {getPartIcon()}
                  <span className="capitalize">{part.type}</span>
                </div>
              </div>
              <div className={`
                inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                bg-white/50 backdrop-blur-sm ${style.text}
              `}>
                <div className="w-1.5 h-1.5 rounded-full mr-1.5 bg-amber-500" />
                <span>{part.status}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center space-x-2">
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(e);
              }}
              className="p-2 text-midnight-400 hover:text-bubblegum-600 hover:bg-bubblegum-50 rounded-lg transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={toggleExpand}
            className="p-2 text-midnight-400 hover:text-midnight-600 hover:bg-white/50 rounded-lg transition-colors"
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="space-y-4">
        {/* Preview Description */}
        <p className="text-midnight-600 whitespace-pre-wrap">
          {previewText}
        </p>

        {/* Emotions Preview */}
        <div className="flex flex-wrap gap-1.5">
          {part.emotions?.slice(0, 3).map(emotion => (
            <span
              key={emotion}
              className={`px-2 py-0.5 bg-white/70 ${style.text} rounded-full text-xs font-medium`}
            >
              {emotion}
            </span>
          ))}
          {part.emotions?.length > 3 && (
            <span className={`px-2 py-0.5 bg-white/70 ${style.text} rounded-full text-xs font-medium`}>
              +{part.emotions.length - 3}
            </span>
          )}
        </div>

        {/* Expanded Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="pt-4 border-t border-white/20 space-y-4"
            >
              {/* Additional Description */}
              {expandedText && (
                <div className="text-midnight-600 whitespace-pre-wrap">
                  {expandedText}
                </div>
              )}

              {/* All Emotions */}
              {part.emotions?.length > 0 && (
                <div className="space-y-2">
                  <h4 className={`text-sm font-medium ${style.text}`}>Emotions</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {part.emotions.map(emotion => (
                      <span
                        key={emotion}
                        className={`px-2 py-0.5 bg-white/70 ${style.text} rounded-full text-xs font-medium`}
                      >
                        {emotion}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {part.notes?.length > 0 && (
                <div className="space-y-2">
                  <h4 className={`text-sm font-medium ${style.text}`}>Notes</h4>
                  <div className="space-y-2">
                    {part.notes.map((note, index) => (
                      <div
                        key={index}
                        className="p-3 bg-white/30 rounded-lg text-midnight-600 text-sm"
                      >
                        {note}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Integrated Parts (for Self) */}
              {isSelf && integratedParts?.length > 0 && (
                <div className="space-y-2">
                  <h4 className={`text-sm font-medium ${style.text}`}>Integrated Parts</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {integratedParts.map(integratedPart => (
                      <button
                        key={integratedPart.id}
                        onClick={(e) => handleClick(e, integratedPart.id)}
                        className="flex items-center space-x-2 p-2 bg-white/30 rounded-lg
                          hover:bg-white/40 transition-colors text-left group/part"
                      >
                        <span className="text-xl group-hover/part:scale-110 transition-transform">
                          {integratedPart.emoji}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${style.text} truncate`}>
                            {integratedPart.name}
                          </p>
                          <p className={`text-xs ${style.text}/80 truncate`}>
                            {integratedPart.type}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}